import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelect } from 'downshift';
import classNames from 'classnames';

import { SvgIcon } from 'common/react/components/SvgIcon';
import { CompIcon } from 'common/react/components/CompIcon';
import { TeamIcon } from 'common/react/components/TeamIcon';
import { getTranslation } from 'common/react/utils/translations';

/**
 * @param {object} props - Props.
 * @param {string} props.label - Label.
 * @param {any[]} props.items - Items.
 * @param {Function} [props.itemToString] - Item to string.
 * @param {any} [props.initialSelectedItem] - Initial selected item.
 * @param {any} [props.activeItem] - Active item from state.
 * @param {Function} [props.onSelectedItemChange] - On change callback.
 * @param {bool} [props.isDisabled] - Is select disabled.
 * @param {bool} [props.showCompLogoInside] - Displays comp logo alongside option
 * @param {object} [props.defaultItem] - Specify object to display as default item
 * @param {Function} [props.onReset] - Reset button handler
 * @returns {JSX.Element} Component.
 */
export const SelectDesktopWithDefault = (props) => {
    const selectRef = useRef();

    const {
        isOpen,
        selectedItem,
        getToggleButtonProps,
        getLabelProps,
        getMenuProps,
        highlightedIndex,
        getItemProps
    } = useSelect({
        items: props.items,
        initialSelectedItem: props.initialSelectedItem,
        selectedItem: props.activeItem,
        // @ts-ignore
        onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
            selectRef?.current.dispatchEvent(
                new Event(
                    PULSE.app.common.CONSTANTS.EVENTS.INTERACTION.CHANGE,
                    { bubbles: true }
                )
            );

            if (typeof props.onSelectedItemChange === 'function') {
                props.onSelectedItemChange({
                    selectedItem: newSelectedItem
                });
            }
        },
        stateReducer
    });

    const [clearActive, setClearActive] = useState(false);

    const currentRound = props?.currentRoundIndex
        ? props.currentRoundIndex
        : false;

    const isDisabled = props?.isDisabled;

    // remove our default item from the list
    const defaultIndex = props.items.indexOf(props.defaultItem);
    if (defaultIndex > -1) {
        props.items.splice(defaultIndex, 1);
    }

    /**
     * @param {any} item - Item.
     * @returns {string} Text.
     */
    const getItemText = (item) =>
        props.itemToString ? props.itemToString(item) : item;

    /**
     *
     * @param state
     * @param actionAndChanges
     */
    function stateReducer(state, actionAndChanges) {
        const { type, changes } = actionAndChanges;
        // this prevents the menu from being closed when the user selects an item with 'Enter' or mouse
        switch (type) {
            case useSelect.stateChangeTypes.ToggleButtonBlur:
                return {
                    ...changes,
                    isOpen: !clearActive ? false : true
                };
            default:
                return changes; // otherwise business as usual.
        }
    }

    /**
     * Check whether 'Clear' button has been selected or not as this determines the blur behaviour.
     */
    document.addEventListener('mousedown', (evt) => {
        if (evt?.target?.classList.contains('select-multiple__header-clear')) {
            setClearActive(true);
        } else {
            setClearActive(false);
        }
    });

    return (
        <div
            className={classNames('select', {
                'select--open': props.isOpen
            })}
            ref={selectRef}
        >
            <button
                type="button"
                {...getToggleButtonProps({
                    id: `${PULSE.app.common.idifyString(
                        props.label
                    )}-dropdown-button`,
                    'aria-labelledby': `${PULSE.app.common.idifyString(
                        props.label
                    )}-dropdown-label`
                })}
                className="select__display"
                disabled={isDisabled}
                onBlur={(evt) => {
                    if (
                        evt?.nativeEvent?.relatedTarget &&
                        evt?.nativeEvent?.relatedTarget?.classList.contains(
                            'select__header-clear'
                        )
                    ) {
                        setClearActive(true);
                    } else {
                        setClearActive(false);
                    }
                }}
            >
                {selectedItem.default && props.icon ? ( // display default icon if default selected
                    <span className={`competition-nav__icon`}>
                        <SvgIcon
                            icon={props.icon}
                            className="icon icon--placeholder"
                        />
                    </span>
                ) : props.showTeamLogo && selectedItem.teamAbbr ? (
                    <TeamIcon
                        className="select__option-logo-icon"
                        teamAbbreviation={selectedItem.teamAbbr}
                    />
                ) : props.showCompLogo && selectedItem?.code ? (
                    <span
                        className={`competition-nav__comp-icon competition-nav__comp-icon--${selectedItem.code.toLowerCase()}`}
                    >
                        <CompIcon code={selectedItem.code} className="icon" />
                    </span>
                ) : null}
                <span className="select__content">
                    {props.label && !props.hideLabel ? (
                        <label
                            {...getLabelProps({
                                id: `${PULSE.app.common.idifyString(
                                    props.label
                                )}-dropdown-label`
                            })}
                            className="select__label"
                        >
                            {props.label}
                        </label>
                    ) : null}
                    <span className="select__current">
                        <span className="select__current-text">
                            {getItemText(selectedItem) || ''}
                        </span>
                        <SvgIcon
                            className="select__chevron"
                            icon="chevron-down"
                        />
                    </span>
                </span>
            </button>
            <div className="select__options-wrapper">
                {isOpen && selectedItem ? (
                    <div
                        className={classNames('select__header', {
                            'u-hide': !isOpen && !clearActive
                        })}
                    >
                        {props.title}
                        {props.defaultItem.id !== selectedItem.id ? ( // if default item not selected, show clear button
                            <button
                                type="button"
                                className="select__header-clear button-new button-new--secondary"
                                onClick={props.onReset}
                            >
                                {getTranslation('label.clear')}
                            </button>
                        ) : null}
                    </div>
                ) : null}
                <ul
                    {...getMenuProps(
                        {
                            'aria-labelledby': `${PULSE.app.common.idifyString(
                                props.label
                            )}-dropdown-label`
                        },
                        { suppressRefError: true }
                    )}
                    className={classNames('select__options-list', {
                        'u-hide': !isOpen && !clearActive
                    })}
                >
                    {isOpen &&
                        props.items?.map((item, index) => {
                            const itemText = getItemText(item);
                            const isSelected =
                                getItemText(selectedItem) === itemText;

                            return (
                                <li
                                    key={`${itemText}${index}`}
                                    {...getItemProps({ item, index })}
                                    className={classNames('select__option', {
                                        'select__option--selected': isSelected,
                                        'select__option--current':
                                            currentRound === index,
                                        'select__option--highlighted':
                                            highlightedIndex === index
                                    })}
                                >
                                    <div className="select__option-logo-name-wrapper">
                                        {props.showTeamLogo &&
                                            item.teamAbbr && (
                                                <TeamIcon
                                                    className="select__option-logo-icon"
                                                    teamAbbreviation={
                                                        item.teamAbbr
                                                    }
                                                />
                                            )}
                                        {props.showCompLogo &&
                                            props.showCompLogoInside &&
                                            selectedItem && (
                                                <span
                                                    className={`select__option-logo-icon--comp competition-nav__comp-icon competition-nav__comp-icon--internal competition-nav__comp-icon--${item?.code?.toLowerCase()}`}
                                                >
                                                    <CompIcon
                                                        code={item?.code}
                                                        className="icon"
                                                    />
                                                </span>
                                            )}
                                        <span>{itemText}</span>
                                    </div>

                                    {isSelected && (
                                        <SvgIcon
                                            icon={`checkmark`}
                                            className="select__option-button-icon"
                                        />
                                    )}
                                </li>
                            );
                        })}
                </ul>
            </div>
        </div>
    );
};

SelectDesktopWithDefault.propTypes = {
    label: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.any),
    itemToString: PropTypes.func,
    initialSelectedItem: PropTypes.any,
    activeItem: PropTypes.any,
    onSelectedItemChange: PropTypes.func,
    currentRoundIndex: PropTypes.number,
    showCompLogo: PropTypes.bool,
    isDisabled: PropTypes.bool,
    hideLabel: PropTypes.bool,
    showTeamLogo: PropTypes.bool,
    isOpen: PropTypes.bool,
    icon: PropTypes.string,
    title: PropTypes.string,
    showCompLogoInside: PropTypes.bool,
    defaultItem: PropTypes.object,
    onReset: PropTypes.func
};
